import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Activity,
  Loader2,
  ExternalLink,
  ImageIcon,
  Copy,
  Check,
  Filter,
  RotateCcw,
  CalendarDays,
  ChevronsUpDown,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import type { ChipOption } from '@/components/chip-group';
import { ChipGroup, FacetLabel } from '@/components/chip-group';
import Container from '@/components/container';
import { CopyCountButton } from '@/components/copy-count-button';
import { OverrideCell } from '@/components/monitor/override-cell';
import type { MonitorStatus } from '@/components/monitor/override-cell';
import { RecentDealLink } from '@/components/recent-deal-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useRecentDealByDomain } from '@/hooks/use-deals';
import { cn } from '@/lib/utils';
import type { Id } from '../../../../../convex/_generated/dataModel';
import { api } from '../../../../../convex/_generated/api';

// ============================================================================
// Status styling
// ============================================================================

const STATUS_COLORS: Record<string, string> = {
  live: 'bg-green-100 text-green-800 hover:bg-green-100',
  parked: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  redirect_in_zone: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  redirect_out_of_zone: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  error: 'bg-red-100 text-red-800 hover:bg-red-100',
  not_in_zone: 'bg-slate-100 text-slate-600 hover:bg-slate-100',
};

const AGREEMENT_COLORS: Record<string, string> = {
  agree: 'bg-green-100 text-green-800 hover:bg-green-100',
  disagree: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  no_baseline: 'bg-slate-100 text-slate-600 hover:bg-slate-100',
};

const AGREEMENT_LABELS: Record<string, string> = {
  agree: 'Agree',
  disagree: 'Disagree',
  no_baseline: 'No baseline',
};

const AGREEMENT_OPTIONS: ChipOption<'agree' | 'disagree' | 'no_baseline'>[] = (['agree', 'disagree', 'no_baseline'] as const).map((f) => ({
  value: f,
  label: AGREEMENT_LABELS[f],
  className: AGREEMENT_COLORS[f],
}));

const COMPARISON_OPTIONS: ChipOption<'legacy'>[] = [{ value: 'legacy', label: 'Show legacy & agreement' }];

function labelize(s: string | null): string {
  if (!s)
    return '—';
  return s.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status)
    return <span className="text-muted-foreground">—</span>;
  return <Badge className={STATUS_COLORS[status] ?? STATUS_COLORS.error}>{labelize(status)}</Badge>;
}

const SUSPICION_COLORS: Record<string, string> = {
  bare_ip_host: 'bg-red-600 text-white hover:bg-red-600',
  unrelated_content: 'bg-red-600 text-white hover:bg-red-600',
};

// Column facets rendered as on-the-fly quick-filter chips: the distinct values
// are derived from the loaded batch. Values within a facet are OR'd; facets are
// AND'd with each other, the Agreement filter, and the search box.
const ZONE_COLORS: Record<string, string> = {
  '.bank': 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  '.insurance': 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100',
};

const FACETS = [
  { key: 'zone', label: 'Zone', colors: ZONE_COLORS },
  { key: 'status', label: 'Worker', colors: STATUS_COLORS },
  { key: 'legacyStatusAtScan', label: 'Legacy', colors: STATUS_COLORS },
  { key: 'classifiedBy', label: 'By' },
  { key: 'suspicion', label: 'Suspicion', colors: SUSPICION_COLORS },
  { key: 'errorSubtype', label: 'Error subtype' },
] as const;

type FacetKey = (typeof FACETS)[number]['key'];

function emptyFacetFilters(): Record<FacetKey, Set<string>> {
  return {
    zone: new Set(),
    status: new Set(),
    legacyStatusAtScan: new Set(),
    classifiedBy: new Set(),
    suspicion: new Set(),
    errorSubtype: new Set(),
  };
}

function FacetChipGroup({ label, options, selected, colors, onToggle }: {
  label: string;
  options: { value: string; count: number }[];
  selected: Set<string>;
  colors?: Record<string, string>;
  onToggle: (value: string) => void;
}) {
  // Nothing worth filtering if the only value present is blank
  if (!options.some((o) => o.value !== ''))
    return null;
  return (
    <ChipGroup
      label={label}
      options={options.map((o) => ({
        value: o.value,
        label: o.value === '' ? '(none)' : labelize(o.value),
        className: o.value ? colors?.[o.value] : undefined,
      }))}
      selected={selected}
      onToggle={onToggle}
      counts={Object.fromEntries(options.map((o) => [o.value, o.count]))}
    />
  );
}

// ============================================================================
// Types (mirror workerScans query shapes)
// ============================================================================

interface ScanRow {
  id: Id<'monitorWorkerScans'>;
  domain: string;
  status: string;
  legacyStatusAtScan: string | null;
  agreement: 'agree' | 'disagree' | 'no_baseline';
  mode: 'shadow' | 'live';
  scannedAt: number;
  tierReached: number | null;
  classifiedBy: string | null;
  confidence: number | null;
  errorSubtype: string | null;
  rationale: string | null;
  suspicion: string | null;
  httpStatusCode: number | null;
  finalUrl: string | null;
  durationMs: number | null;
  hasScreenshot: boolean;
  workerId: string;
}

/** ScanRow joined with the domain's current monitor override (per-domain, not per-scan). */
interface GridRow extends ScanRow {
  hasActiveOverride: boolean;
  overrideStatus: MonitorStatus | null;
}

// ============================================================================
// Detail sheet
// ============================================================================

function EvidenceBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-0.5 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="break-words">{v}</span>
    </div>
  );
}

function ScanDetailSheet({
  scanId,
  open,
  onOpenChange,
}: {
  scanId: Id<'monitorWorkerScans'> | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const detail = useQuery(api.workerScans.getWorkerScanDetail, scanId ? { id: scanId } : 'skip');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        {detail == null
          ? (
              <div className="flex h-40 items-center justify-center text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            )
          : (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <a
                      href={`https://${detail.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {detail.domain}
                      <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                    <StatusBadge status={detail.status} />
                  </SheetTitle>
                  <SheetDescription>
                    {format(new Date(detail.scannedAt), 'PPpp')}
                    {' · '}
                    tier
                    {' '}
                    {detail.tierReached ?? '—'}
                    {' · '}
                    {detail.classifiedBy ?? 'unclassified'}
                    {detail.confidence != null && ` · ${Math.round(detail.confidence * 100)}% conf`}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-4 space-y-3">
                  <EvidenceBlock title="Verdict">
                    <KV k="Worker status" v={<StatusBadge status={detail.status} />} />
                    <KV k="Legacy at scan" v={<StatusBadge status={detail.legacyStatusAtScan} />} />
                    <KV
                      k="Agreement"
                      v={<Badge className={AGREEMENT_COLORS[detail.agreement]}>{AGREEMENT_LABELS[detail.agreement]}</Badge>}
                    />
                    {detail.suspicion && (
                      <KV
                        k="Suspicion"
                        v={<Badge className="bg-red-600 text-white hover:bg-red-600">{labelize(detail.suspicion)}</Badge>}
                      />
                    )}
                    {detail.errorSubtype && <KV k="Error subtype" v={labelize(detail.errorSubtype)} />}
                    {detail.rationale && <KV k="Rationale" v={detail.rationale} />}
                    <KV k="Mode" v={detail.mode} />
                    <KV
                      k="Final URL"
                      v={detail.finalUrl
                        ? (
                            <a
                              href={detail.finalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="break-all text-primary underline underline-offset-2 hover:opacity-80"
                            >
                              {detail.finalUrl}
                              <ExternalLink className="ml-1 inline size-3.5 align-[-2px]" />
                            </a>
                          )
                        : '—'}
                    />
                    <KV k="HTTP status" v={detail.httpStatusCode ?? '—'} />
                    <KV k="Duration" v={detail.durationMs == null ? '—' : `${detail.durationMs} ms`} />
                  </EvidenceBlock>

                  {detail.screenshotUrl && (
                    <EvidenceBlock title="Screenshot">
                      <a href={detail.screenshotUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={detail.screenshotUrl}
                          alt={`${detail.domain} screenshot`}
                          className="w-full rounded border"
                        />
                      </a>
                    </EvidenceBlock>
                  )}

                  {detail.evidence != null && (
                    <EvidenceBlock title="Raw evidence">
                      <pre className="max-h-96 overflow-auto rounded bg-muted p-2 text-xs">
                        {JSON.stringify(detail.evidence, null, 2)}
                      </pre>
                    </EvidenceBlock>
                  )}
                </div>
              </>
            )}
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Header actions
// ============================================================================

function WorkerActions() {
  const control = useQuery(api.monitorControl.getWorkerControl, {});
  const setMode = useMutation(api.monitorControl.setWorkerMode);
  const setEnabled = useMutation(api.monitorControl.setWorkerEnabled);

  const [cutoverOpen, setCutoverOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const isLive = control?.mode === 'live';
  const enabled = control?.enabled ?? true;
  const targetMode = isLive ? 'shadow' : 'live';
  const confirmWord = targetMode.toUpperCase();

  const handleCutover = useCallback(async () => {
    try {
      await setMode({ mode: targetMode });
      toast.success(`Worker mode set to ${targetMode}`);
      setCutoverOpen(false);
      setConfirmText('');
    } catch {
      toast.error('Failed to change mode');
    }
  }, [setMode, targetMode]);

  const handleToggleEnabled = useCallback(async (next: boolean) => {
    try {
      await setEnabled({ enabled: next });
      toast.success(next ? 'Worker enabled' : 'Worker disabled (AWS scanner will cover)');
    } catch {
      toast.error('Failed to toggle worker');
    }
  }, [setEnabled]);

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            onCheckedChange={handleToggleEnabled}
            disabled={!control}
            id="worker-enabled"
          />
          <label htmlFor="worker-enabled" className="text-sm text-muted-foreground">
            {enabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>
        <Button
          size="sm"
          variant={isLive ? 'outline' : 'default'}
          onClick={() => setCutoverOpen(true)}
          disabled={!control}
        >
          {isLive ? 'Revert to shadow' : 'Cut over to live'}
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/monitor">
            <Activity className="mr-2 size-4" />
            Back to Monitor
          </Link>
        </Button>
      </div>

      <Dialog
        open={cutoverOpen}
        onOpenChange={(o) => {
          setCutoverOpen(o);
          if (!o)
            setConfirmText('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isLive ? 'Revert worker to shadow mode?' : 'Cut worker over to live mode?'}
            </DialogTitle>
            <DialogDescription>
              {isLive
                ? 'The worker will stop driving domain status; the AWS scanner resumes as the authoritative daily scan.'
                : 'The worker becomes the source of truth for domain status. The AWS scanner drops to the 11pm coverage fallback. Make sure the disagreement numbers are understood before proceeding.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">
              Type
              {' '}
              <span className="font-mono font-semibold">{confirmWord}</span>
              {' '}
              to confirm.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmWord}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCutoverOpen(false);
                setConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant={isLive ? 'default' : 'destructive'}
              disabled={confirmText.trim().toUpperCase() !== confirmWord}
              onClick={handleCutover}
            >
              {isLive ? 'Revert to shadow' : 'Cut over to live'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// Batch coverage (jobs enqueued vs. scans the grid can actually show)
// ============================================================================

type MissingOutcome = 'failed' | 'aws_fallback' | 'pending' | 'leased';

const OUTCOME_LABELS: Record<MissingOutcome, string> = {
  failed: 'Failed (attempts exhausted)',
  aws_fallback: 'Covered by AWS fallback',
  pending: 'Still pending',
  leased: 'In progress',
};

const OUTCOME_COLORS: Record<MissingOutcome, string> = {
  failed: 'bg-red-100 text-red-800 hover:bg-red-100',
  aws_fallback: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  pending: 'bg-slate-100 text-slate-600 hover:bg-slate-100',
  leased: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
};

interface BatchCoverage {
  batchDate: string;
  jobCount: number;
  scanned: number;
  awsFallback: number;
  failed: number;
  pending: number;
  leased: number;
  missing: Array<{
    domain: string;
    kind: 'bank' | 'insurance';
    outcome: MissingOutcome;
    attempts: number;
    completedAt: number | null;
  }>;
}

/**
 * The grid only lists jobs the worker successfully submitted. This strip shows
 * how many jobs the batch actually had and where the rest went, so a short
 * batch reads as "13 failed" instead of a mysteriously smaller number.
 */
function CoverageStrip({ coverage, loadedRows }: { coverage: BatchCoverage | undefined; loadedRows: number }) {
  const [open, setOpen] = useState(false);

  if (!coverage) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        Loading coverage…
      </div>
    );
  }

  const unfinished = coverage.pending + coverage.leased;
  const missingCount = coverage.missing.length;
  const complete = missingCount === 0;
  const hasProblems = coverage.failed > 0 || coverage.awsFallback > 0;
  let tone = 'text-muted-foreground';
  if (!complete)
    tone = hasProblems ? 'text-red-700' : 'text-amber-700';

  const handleCopyMissing = () => {
    void navigator.clipboard.writeText(coverage.missing.map((m) => m.domain).join('\n'));
    toast.success(`Copied ${missingCount} domains`);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {complete
          ? <CheckCircle2 className="size-3.5 text-green-600" />
          : <AlertTriangle className={cn('size-3.5', hasProblems ? 'text-red-600' : 'text-amber-600')} />}
        <span className={cn('font-medium', tone)}>
          {coverage.scanned.toLocaleString()}
          {' of '}
          {coverage.jobCount.toLocaleString()}
          {' jobs scanned by worker'}
        </span>
        {coverage.failed > 0 && (
          <Badge className={OUTCOME_COLORS.failed}>
            {coverage.failed}
            {' failed'}
          </Badge>
        )}
        {coverage.awsFallback > 0 && (
          <Badge className={OUTCOME_COLORS.aws_fallback}>
            {coverage.awsFallback}
            {' AWS fallback'}
          </Badge>
        )}
        {unfinished > 0 && (
          <Badge className={OUTCOME_COLORS.leased}>
            {unfinished}
            {' unfinished'}
          </Badge>
        )}
        {!complete && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-primary underline-offset-2 hover:underline"
          >
            View
            {' '}
            {missingCount}
            {' '}
            missing
          </button>
        )}
        {loadedRows !== coverage.scanned && (
          <span className="text-muted-foreground">
            (
            {loadedRows.toLocaleString()}
            {' loaded so far)'}
          </span>
        )}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              Missing from
              {' '}
              {coverage.batchDate}
            </SheetTitle>
            <SheetDescription>
              {missingCount}
              {' of '}
              {coverage.jobCount.toLocaleString()}
              {' jobs have no worker scan in this batch. '}
              Failed jobs hit the 3-attempt limit (usually a submit error, not a scan error);
              AWS-fallback jobs were taken over by the 11pm sweep and their status lives in the main monitor.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-wrap gap-1.5">
              {(['failed', 'aws_fallback', 'pending', 'leased'] as const).map((o) => {
                const n = coverage.missing.filter((m) => m.outcome === o).length;
                if (n === 0)
                  return null;
                return (
                  <Badge key={o} className={OUTCOME_COLORS[o]}>
                    {n}
                    {' '}
                    {OUTCOME_LABELS[o]}
                  </Badge>
                );
              })}
            </div>
            <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={handleCopyMissing}>
              <Copy className="size-3.5" />
              Copy domains
            </Button>
          </div>
          <div className="mt-4 divide-y rounded-lg border">
            {coverage.missing.map((m) => (
              <div key={m.domain} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <a
                  href={`https://${m.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate font-medium text-primary hover:underline"
                >
                  {m.domain}
                </a>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {m.attempts}
                    {' '}
                    {m.attempts === 1 ? 'attempt' : 'attempts'}
                  </span>
                  <Badge className={OUTCOME_COLORS[m.outcome]}>{labelize(m.outcome)}</Badge>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ============================================================================
// Main page
// ============================================================================

type AgreementFilter = 'all' | 'agree' | 'disagree' | 'no_baseline';

// Rows per page while auto-loading a batch. Scan documents carry large
// evidence blobs (counted against the query's read budget even though the
// list query doesn't return them), so pages are kept moderate.
const SCAN_PAGE_SIZE = 500;

function DetailedScansPage() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const [selectedBatch, setSelectedBatch] = useState<string | undefined>();
  const [agreementFilter, setAgreementFilter] = useState<AgreementFilter>('all');
  const [facetFilters, setFacetFilters] = useState<Record<FacetKey, Set<string>>>(emptyFacetFilters);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailId, setDetailId] = useState<Id<'monitorWorkerScans'> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [batchPickerOpen, setBatchPickerOpen] = useState(false);
  const [legacyCompareOn, setLegacyCompareOn] = useState(false);

  // Once the worker is live, the legacy scanner is no longer the baseline, so
  // the Legacy / Agreement column and chips are opt-in via a toggle chip.
  const control = useQuery(api.monitorControl.getWorkerControl, {});
  const isLive = control?.mode === 'live';
  const showLegacyCompare = !isLive || legacyCompareOn;

  // While the legacy/agreement controls are hidden their filters are ignored
  // (not cleared) so they don't silently narrow the grid but come back intact
  // if comparison is toggled on again.
  const effectiveAgreementFilter: AgreementFilter = showLegacyCompare ? agreementFilter : 'all';
  const activeFacets = useMemo(
    () => FACETS.filter((f) => showLegacyCompare || f.key !== 'legacyStatusAtScan'),
    [showLegacyCompare],
  );

  // useRecentDealByDomain returns a fresh function each render (useAxios is not
  // memoized); route through a ref so columnDefs don't rebuild every render.
  const { getRecentDeal } = useRecentDealByDomain();
  const getRecentDealRef = useRef(getRecentDeal);
  getRecentDealRef.current = getRecentDeal;
  const lookupRecentDeal = useCallback(async (domain: string) => getRecentDealRef.current(domain), []);

  useEffect(() => {
    const path = '/app/monitor/detailed-scans';
    setLabel(path, { name: 'Detailed Scans', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  const batches = useQuery(api.workerScans.listBatches, {});

  // Default to the active batch (what the worker is filling right now), then
  // the newest known batch. Resolved client-side so the paginated scan query
  // can take a concrete batchDate.
  const effectiveBatch = selectedBatch ?? control?.currentBatch ?? batches?.[0]?.batchDate ?? null;
  const batchResolved = control !== undefined && batches !== undefined;

  // Load the whole batch page by page. A single query used to `.take()` the
  // batch and topped out around 2,500 rows against Convex's read limits.
  const {
    results: scanPages,
    status: scanLoadStatus,
    loadMore: loadMoreScans,
  } = usePaginatedQuery(
    api.workerScans.listWorkerScans,
    effectiveBatch ? { batchDate: effectiveBatch } : 'skip',
    { initialNumItems: SCAN_PAGE_SIZE },
  );
  useEffect(() => {
    if (scanLoadStatus === 'CanLoadMore')
      loadMoreScans(SCAN_PAGE_SIZE);
  }, [scanLoadStatus, loadMoreScans]);

  const scans = useMemo(() => scanPages as ScanRow[], [scanPages]);

  const coverage = useQuery(
    api.scanJobs.getBatchCoverage,
    effectiveBatch ? { batchDate: effectiveBatch } : 'skip',
  ) as BatchCoverage | undefined;

  const overrides = useQuery(api.monitor.getAllActiveOverrides);
  const upsertOverride = useMutation(api.monitor.upsertStatusOverride);
  const deactivateOverride = useMutation(api.monitor.deactivateOverride);

  const overrideMap = useMemo(() => {
    const map = new Map<string, MonitorStatus>();
    for (const o of overrides ?? []) map.set(o.domain, o.statusOverride as MonitorStatus);
    return map;
  }, [overrides]);

  const handleSetOverride = useCallback(async (domain: string, status: MonitorStatus) => {
    try {
      await upsertOverride({ domain, statusOverride: status, active: true });
      toast.success(`Override set to "${status.replaceAll('_', ' ')}" for ${domain}`);
    } catch {
      toast.error('Failed to set override');
    }
  }, [upsertOverride]);

  const handleClearOverride = useCallback(async (domain: string) => {
    try {
      await deactivateOverride({ domain });
      toast.success(`Override cleared for ${domain}`);
    } catch {
      toast.error('Failed to clear override');
    }
  }, [deactivateOverride]);

  const facetValue = useCallback((row: ScanRow, key: FacetKey): string => {
    if (key === 'zone') {
      if (row.domain.endsWith('.bank'))
        return '.bank';
      if (row.domain.endsWith('.insurance'))
        return '.insurance';
      return '';
    }
    const raw = row[key];
    return raw == null || raw === '' ? '' : String(raw);
  }, []);

  const rows = useMemo((): GridRow[] => {
    return scans
      .filter((s) => {
        if (effectiveAgreementFilter !== 'all' && s.agreement !== effectiveAgreementFilter)
          return false;
        for (const f of activeFacets) {
          const sel = facetFilters[f.key];
          if (sel.size > 0 && !sel.has(facetValue(s, f.key)))
            return false;
        }
        return true;
      })
      .map((s) => ({
        ...s,
        hasActiveOverride: overrideMap.has(s.domain),
        overrideStatus: overrideMap.get(s.domain) ?? null,
      }));
  }, [scans, effectiveAgreementFilter, activeFacets, facetFilters, facetValue, overrideMap]);

  // Distinct values + counts per facet, derived from the whole batch (matches
  // how the Agreement chip counts are computed).
  const facetOptions = useMemo(() => {
    const result = {} as Record<FacetKey, { value: string; count: number }[]>;
    for (const f of FACETS) {
      const counts = new Map<string, number>();
      for (const s of scans) {
        const v = facetValue(s, f.key);
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      result[f.key] = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({ value, count }));
    }
    return result;
  }, [scans, facetValue]);

  const toggleFacet = useCallback((key: FacetKey, value: string) => {
    setFacetFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value))
        next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  }, []);

  const openDetail = useCallback((id: Id<'monitorWorkerScans'>) => {
    setDetailId(id);
    setDetailOpen(true);
  }, []);

  const columnDefs = useMemo((): ColDef<GridRow>[] => [
    {
      field: 'domain',
      headerName: 'Domain',
      minWidth: 200,
      filter: true,
      cellRenderer: (p: ICellRendererParams<GridRow>) => (
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => p.data && openDetail(p.data.id)}
        >
          {p.value}
        </button>
      ),
    },
    {
      colId: 'deal',
      headerName: 'Deal',
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        if (!p.data)
          return null;
        // Padding is measured by the fitCellContents auto-size, giving the fit its margin
        return (
          <div className="px-1.5">
            <RecentDealLink domain={p.data.domain} getRecentDeal={lookupRecentDeal} />
          </div>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Worker',
      minWidth: 140,
      filter: true,
      cellRenderer: (p: ICellRendererParams<GridRow>) => <StatusBadge status={p.value} />,
    },
    {
      field: 'legacyStatusAtScan',
      headerName: 'Legacy @ scan',
      minWidth: 140,
      filter: true,
      hide: !showLegacyCompare,
      cellRenderer: (p: ICellRendererParams<GridRow>) => <StatusBadge status={p.value} />,
    },
    {
      field: 'agreement',
      headerName: 'Agreement',
      minWidth: 120,
      filter: true,
      hide: !showLegacyCompare,
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value
          ? <Badge className={AGREEMENT_COLORS[p.value]}>{AGREEMENT_LABELS[p.value]}</Badge>
          : null,
    },
    {
      // Keyed on the status (not the boolean) so AG Grid sees a value change and
      // repaints the cell when an override switches from one status to another.
      field: 'overrideStatus',
      headerName: 'Override',
      minWidth: 130,
      filter: true,
      valueFormatter: (p) => (p.value ? labelize(p.value) : 'None'),
      cellRenderer: (p: ICellRendererParams<GridRow>) => (
        <OverrideCell
          domain={p.data?.domain || ''}
          hasOverride={p.data?.hasActiveOverride || false}
          overrideStatus={p.data?.overrideStatus ?? null}
          onSetOverride={handleSetOverride}
          onClearOverride={handleClearOverride}
        />
      ),
    },
    {
      headerName: '',
      minWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        if (!p.data)
          return null;
        return (
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2" onClick={() => openDetail(p.data!.id)}>
            {p.data.hasScreenshot ? <ImageIcon className="size-4" /> : <ExternalLink className="size-4" />}
            <span className="text-xs">View</span>
          </Button>
        );
      },
    },
    {
      field: 'scannedAt',
      headerName: 'Scanned',
      minWidth: 160,
      sort: 'desc',
      valueFormatter: (p) => (p.value ? formatDistanceToNow(new Date(p.value), { addSuffix: true }) : '—'),
    },
    {
      field: 'tierReached',
      headerName: 'Tier',
      minWidth: 80,
      valueFormatter: (p) => (p.value == null ? '—' : String(p.value)),
    },
    {
      field: 'classifiedBy',
      headerName: 'By',
      minWidth: 110,
      valueFormatter: (p) => p.value ?? '—',
    },
    {
      field: 'confidence',
      headerName: 'Conf',
      minWidth: 90,
      valueFormatter: (p) => (p.value == null ? '—' : `${Math.round(p.value * 100)}%`),
    },
    {
      field: 'suspicion',
      headerName: 'Suspicion',
      minWidth: 140,
      filter: true,
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value
          ? <Badge className="bg-red-600 text-white hover:bg-red-600">{labelize(p.value)}</Badge>
          : null,
    },
    {
      field: 'errorSubtype',
      headerName: 'Error subtype',
      minWidth: 150,
      valueFormatter: (p) => labelize(p.value),
    },
    {
      field: 'finalUrl',
      headerName: 'Final URL',
      minWidth: 240,
      filter: true,
      tooltipField: 'finalUrl',
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value
          ? (
              <a
                href={p.value}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="truncate">{p.value}</span>
                <ExternalLink className="size-3 shrink-0" />
              </a>
            )
          : <span className="text-muted-foreground">—</span>,
    },
    {
      field: 'rationale',
      headerName: 'Rationale',
      minWidth: 280,
      flex: 1,
      tooltipField: 'rationale',
      valueFormatter: (p) => p.value ?? '—',
    },
  ], [openDetail, handleSetOverride, handleClearOverride, lookupRecentDeal, showLegacyCompare]);

  const getRowId = useCallback((p: { data: GridRow }) => p.data.id, []);

  const agreementCounts = useMemo(() => {
    const c = { all: scans.length, agree: 0, disagree: 0, no_baseline: 0 };
    for (const s of scans) c[s.agreement]++;
    return c;
  }, [scans]);

  const hasActiveFilters
    = effectiveAgreementFilter !== 'all'
      || searchQuery.trim() !== ''
      || activeFacets.some((f) => facetFilters[f.key].size > 0);

  const clearFilters = useCallback(() => {
    setAgreementFilter('all');
    setSearchQuery('');
    setFacetFilters(emptyFacetFilters());
  }, []);

  // Export the currently filtered scans (chip filters applied, search box not) to Excel
  const handleExportReport = useCallback(() => {
    if (rows.length === 0) {
      toast.error('No scans to export');
      return;
    }

    try {
      const exportRows = rows.map((row) => ({
        'Scanned': format(new Date(row.scannedAt), 'MM/dd/yyyy h:mm a'),
        'Domain': row.domain,
        'Zone': facetValue(row, 'zone'),
        'Worker Status': labelize(row.status),
        'Legacy @ Scan': row.legacyStatusAtScan ? labelize(row.legacyStatusAtScan) : '',
        'Agreement': AGREEMENT_LABELS[row.agreement],
        'Mode': row.mode,
        'Tier': row.tierReached ?? '',
        'Classified By': row.classifiedBy ?? '',
        'Confidence (%)': row.confidence == null ? '' : Math.round(row.confidence * 100),
        'Suspicion': row.suspicion ? labelize(row.suspicion) : '',
        'Error Subtype': row.errorSubtype ? labelize(row.errorSubtype) : '',
        'Rationale': row.rationale ?? '',
        'HTTP Status': row.httpStatusCode ?? '',
        'Final URL': row.finalUrl ?? '',
        'Duration (ms)': row.durationMs ?? '',
        'Screenshot': row.hasScreenshot ? 'Yes' : 'No',
        'Override': row.hasActiveOverride && row.overrideStatus ? labelize(row.overrideStatus) : 'No',
      }));

      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);

      ws['!cols'] = [
        { wch: 18 }, // Scanned
        { wch: 28 }, // Domain
        { wch: 11 }, // Zone
        { wch: 20 }, // Worker Status
        { wch: 20 }, // Legacy @ Scan
        { wch: 12 }, // Agreement
        { wch: 8 }, // Mode
        { wch: 6 }, // Tier
        { wch: 14 }, // Classified By
        { wch: 14 }, // Confidence
        { wch: 18 }, // Suspicion
        { wch: 18 }, // Error Subtype
        { wch: 60 }, // Rationale
        { wch: 11 }, // HTTP Status
        { wch: 48 }, // Final URL
        { wch: 13 }, // Duration
        { wch: 11 }, // Screenshot
        { wch: 20 }, // Override
      ];

      utils.book_append_sheet(wb, ws, 'Detailed Scans');

      const batchLabel = effectiveBatch ?? 'no_batch';
      const agreementLabel = agreementFilter === 'all' ? '' : `_${agreementFilter}`;
      const filename = `detailed_scans_${batchLabel}${agreementLabel}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;

      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length} scans to ${filename}`);
    } catch {
      toast.error('Failed to export report');
    }
  }, [rows, facetValue, effectiveBatch, agreementFilter]);

  // First page still in flight (or batch not yet resolved). Later pages stream
  // into the grid; the coverage strip shows "N loaded so far" meanwhile.
  const isLoading = !batchResolved || (effectiveBatch !== null && scanLoadStatus === 'LoadingFirstPage');
  const isLoadingMore = scanLoadStatus === 'LoadingMore' || scanLoadStatus === 'CanLoadMore';

  return (
    <Container className="pb-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Detailed Scans</h1>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportReport}
            disabled={isLoading || rows.length === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export
            {' '}
            {rows.length.toLocaleString()}
            {' '}
            scans
          </Button>
          <WorkerActions />
        </div>
      </div>

      <Card className="mb-6 border-dashed">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            {/* Filter groups */}
            <div className="flex flex-wrap gap-x-8 gap-y-4">
              {/* Batch */}
              <div className="flex flex-col gap-1.5">
                <FacetLabel>Batch</FacetLabel>
                <Popover open={batchPickerOpen} onOpenChange={setBatchPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={batchPickerOpen}
                      className="h-8 w-[190px] justify-between text-xs font-normal"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <CalendarDays className="size-3.5 shrink-0 text-muted-foreground" />
                        {effectiveBatch ?? 'Batch date'}
                      </span>
                      <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search batches..." className="h-9" />
                      <CommandEmpty>No batches found.</CommandEmpty>
                      <CommandGroup className="max-h-[260px] overflow-y-auto">
                        {(batches ?? []).map((b) => (
                          <CommandItem
                            key={b.batchDate}
                            value={b.batchDate}
                            onSelect={() => {
                              setSelectedBatch(b.batchDate);
                              setBatchPickerOpen(false);
                            }}
                            className="gap-2 text-xs"
                          >
                            <span className="flex-1 truncate font-mono">{b.batchDate}</span>
                            <span
                              className={cn(
                                'tabular-nums',
                                b.scanCount < b.jobCount ? 'text-amber-700' : 'text-muted-foreground',
                              )}
                              title={b.scanCount < b.jobCount ? `${b.jobCount - b.scanCount} jobs without a worker scan` : undefined}
                            >
                              {b.scanCount}
                              <span className="text-muted-foreground">
                                /
                                {b.jobCount}
                              </span>
                            </span>
                            {effectiveBatch === b.batchDate && <Check className="size-3.5 text-primary" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Legacy comparison toggle (only relevant once the worker is live) */}
              {isLive && (
                <ChipGroup
                  label="Comparison"
                  options={COMPARISON_OPTIONS}
                  selected={legacyCompareOn ? ['legacy'] : []}
                  onToggle={() => setLegacyCompareOn((v) => !v)}
                />
              )}

              {showLegacyCompare && (
                <ChipGroup
                  label="Agreement"
                  options={AGREEMENT_OPTIONS}
                  selected={agreementFilter === 'all' ? [] : [agreementFilter]}
                  onToggle={setAgreementFilter}
                  counts={agreementCounts}
                  all={{ onClick: () => setAgreementFilter('all'), count: agreementCounts.all }}
                />
              )}

              {/* Column facets (Worker, Legacy, By, Suspicion, Error subtype) */}
              {activeFacets.map((f) => (
                <FacetChipGroup
                  key={f.key}
                  label={f.label}
                  options={facetOptions[f.key]}
                  selected={facetFilters[f.key]}
                  colors={'colors' in f ? f.colors : undefined}
                  onToggle={(v) => toggleFacet(f.key, v)}
                />
              ))}
            </div>

            {/* Coverage: jobs enqueued vs. scans this grid can show */}
            {effectiveBatch && (
              <div className="border-t pt-3">
                <CoverageStrip coverage={coverage} loadedRows={scans.length} />
              </div>
            )}

            {/* Controls bar */}
            <div className="flex items-center justify-between gap-3 border-t pt-3">
              <div className="flex items-center gap-2">
                {hasActiveFilters
                  ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="gap-1.5 text-xs text-muted-foreground"
                      >
                        <RotateCcw className="size-3" />
                        Clear filters
                      </Button>
                    )
                  : (
                      <span className="text-xs text-muted-foreground">
                        Filter by agreement or any column facet, or pick a batch date
                      </span>
                    )}
              </div>

              <div className="flex items-center gap-3">
                <CopyCountButton
                  count={rows.length}
                  label="scans"
                  loadingHint={isLoadingMore ? 'Still loading the batch…' : undefined}
                />

                <div className="relative w-56">
                  <Input
                    type="search"
                    placeholder="Search scans by domain..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                  <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading worker scans…</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <AGDataGrid
              columnDefs={columnDefs}
              rowData={rows}
              quickFilterText={searchQuery}
              getRowId={getRowId}
              autoSizeStrategy={{ type: 'fitCellContents', colIds: ['deal'] }}
              overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No worker scans for this batch yet</span>'
            />
          )}

      <ScanDetailSheet scanId={detailId} open={detailOpen} onOpenChange={setDetailOpen} />
    </Container>
  );
}

export default DetailedScansPage;
